import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaFloppyDisk } from 'react-icons/fa6';
import ApplicationProfileFields from '../../Componant/ApplicationProfileFields';
import { get_application_profile, update_application_profile } from '../../Services/Student/StudentApi';
import { getApplicationError, getApplicationProfileChanges, isApiFailure, normalizeApplicationProfile, validateApplicationAnswers } from '../../utils/applicationProfile';

const ApplicationProfileEditor = ({ onClose }) => {
  const [value, setValue] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [success, setSuccess] = useState('');
  const [attempt, setAttempt] = useState(0);
  const mounted = useRef(false);
  const saveInProgress = useRef(false);
  const [footerTarget, setFooterTarget] = useState(null);
  const formId = 'saved-application-profile-form';

  useEffect(() => {
    mounted.current = true;
    setFooterTarget(document.getElementById('application-profile-footer'));
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setMessage('');
    const load = async () => {
      try {
        const response = await get_application_profile();
        if (!current) return;
        if (isApiFailure(response) || !response.application_profile) {
          setMessage(getApplicationError(response, 'Unable to load application details. Please retry.').message);
          return;
        }
        const profile = normalizeApplicationProfile(response.application_profile);
        setOriginal(profile);
        setValue(profile);
      } catch {
        if (current) setMessage('Unable to load application details. Please retry.');
      } finally {
        if (current) setLoading(false);
      }
    };
    load();
    return () => { current = false; };
  }, [attempt]);

  const save = async event => {
    event.preventDefault();
    if (!value || saveInProgress.current) return;
    const validation = validateApplicationAnswers(value);
    setErrors(validation);
    setMessage('');
    setSuccess('');
    if (Object.keys(validation).length) return;
    const changes = getApplicationProfileChanges(original, value);
    if (!Object.keys(changes).length) { setSuccess('No changes to save.'); return; }
    saveInProgress.current = true;
    setSaving(true);
    try {
      const response = await update_application_profile(changes);
      if (!mounted.current) return;
      if (isApiFailure(response) || !response.application_profile) {
        const error = getApplicationError(response, 'Unable to save application details. Please retry.');
        setErrors(error.details);
        setMessage(error.message);
        return;
      }
      const saved = normalizeApplicationProfile(response.application_profile);
      setValue(saved);
      setOriginal(saved);
      setSuccess('Application details saved.');
    } catch {
      if (mounted.current) setMessage('Unable to save application details. Please retry.');
    } finally {
      saveInProgress.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const actions = (
    <div className="application-profile-actions">
      {value && <p className={`application-profile-actions__status${success ? ' application-profile-actions__status--success' : ''}`} role={success ? 'status' : undefined}>{success || 'You can save an incomplete draft.'}</p>}
      <div className="application-profile-actions__buttons">
        {onClose && <button type="button" className="application-profile-action" onClick={onClose}>Close</button>}
        {!loading && value && <button type="submit" form={formId} className="application-profile-action application-profile-action--save" disabled={saving}><FaFloppyDisk aria-hidden="true" />{saving ? 'Saving...' : 'Save Application Details'}</button>}
      </div>
    </div>
  );

  return (
    <form id={formId} className="application-profile-editor" onSubmit={save}>
      <div className="application-profile-editor__header">
        <h5>Application Details</h5>
        <p>Keep your go-to answers ready for your next application. Review and update them for each job.</p>
      </div>
      {loading && <p role="status">Loading application details...</p>}
      {message && <div className="alert alert-danger" role="alert"><strong>Alert!</strong> {message}</div>}
      {!loading && !value && <button type="button" className="profileBtn" onClick={() => setAttempt(prev => prev + 1)}>Retry</button>}
      {!loading && value && <>
        <ApplicationProfileFields value={value} onChange={next => { setValue(next); setErrors({}); setSuccess(''); }} errors={errors} disclosures disabled={saving} idPrefix="saved-application" />
      </>}
      {footerTarget ? createPortal(actions, footerTarget) : actions}
    </form>
  );
};

export default ApplicationProfileEditor;
